'use client';
import React, { useState } from 'react';
// import { FlexWrapper } from '@intigral/shared';
import { FlexWrapper } from '../FlexWrapper';
import styles from './activity.module.sass';
import cn from 'classnames';
import { FontIcon } from '../FontIcon';
import { Skeleton } from '../Skeleton';

interface HistoryItem {
    title?: string;
    channelName?: string;
    ts: number;
    historyType: string;
    seriesTitle?: string;
    episodeNumber?: string;
    seasonNumber?: string;
}

interface Activity {
    date: string;
    value: string;
    type: string;
}

interface ActivityListProps extends React.HTMLAttributes<HTMLDivElement> {
    heading: string | React.ReactNode;
    seeMoreText: string;
    visibleQuantity?: number;
    onClick?: (event: React.SyntheticEvent) => void;
    historyData: HistoryItem[]; // Directly accepting historyResponse data
    unsorted?: boolean;
    isLoading?: boolean;
}

function getIconForType(historyType: string) {
    switch (historyType) {
        case 'MOVIE':
            return <FontIcon name="movie" color="gray" size="smaller" md="smaller" />;
        case 'CHANNEL':
            return <FontIcon name="live" color="gray" size="smaller" md="smaller" />;
        case 'SERIES':
            return <FontIcon name="episode-list" color="gray" size="smaller" md="smaller" />;
        default:
            return null; // Default case if the type is unknown
    }
}

function getTitle(item: HistoryItem) {
    switch (item.historyType) {
        case 'MOVIE':
            return item.title;
        case 'CHANNEL':
            return item.channelName;
        case 'SERIES':
            return `${item.episodeNumber}. ${item.title} - ${item.seasonNumber}, ${item.seriesTitle} `;
        default:
            return 'No Title'; // Default case if the type is unknown
    }
}

export const ActivityList = ({
    heading,
    seeMoreText,
    visibleQuantity = 5,
    onClick,
    historyData,
    unsorted,
    isLoading,
    ...props
}: ActivityListProps) => {
    const [displayCount, setDisplayCount] = useState(visibleQuantity);

    const handleShowMore = (event: React.SyntheticEvent) => {
        setDisplayCount((prevCount) => prevCount + visibleQuantity);
        if (onClick) {
            onClick(event);
        }
    };

    if (!historyData || historyData.length === 0) {
        return null;
    }

    const data = historyData.map((item) => {
        const value = getTitle(item);
        const date = new Date(item.ts).toLocaleDateString(); // Convert timestamp to readable date
        const type = item.historyType;
        return { date, value, type };
    });

    const displayData = data.slice(0, displayCount);
    const canShowMore = displayData.length < data.length;

    if (isLoading) {
        return (
            <FlexWrapper flexDirection="column" className={styles.block} {...props}>
                <div className={cn(styles.list, unsorted && styles.unsorted)}>
                    {Array.from({ length: 10 }).map((_, index) => (
                        <React.Fragment key={index}>
                            <span className={styles.date}>
                                <Skeleton width={60} height={24} />
                            </span>
                            {unsorted && <Skeleton width={24} height={24} />}
                            <span>
                                <Skeleton height={24} />
                            </span>
                        </React.Fragment>
                    ))}
                </div>
            </FlexWrapper>
        );
    }

    return (
        <FlexWrapper flexDirection="column" className={styles.block} {...props}>
            {typeof heading === 'string' ? <strong className={styles.heading}>{heading}</strong> : heading}
            <div className={cn(styles.list, unsorted && styles.unsorted)}>
                {displayData.map((activity, index) => (
                    <React.Fragment key={index}>
                        <span className={styles.date}>{activity.date}</span>
                        {unsorted && getIconForType(activity.type)}
                        <span>{activity.value}</span>
                    </React.Fragment>
                ))}
            </div>
            <div onClick={(event) => handleShowMore(event)} className={cn(styles.more, !canShowMore && styles.hidden)}>
                {seeMoreText}
            </div>
        </FlexWrapper>
    );
};
